import { cn } from '@/lib/utils'
import { Search, Bell, Upload, Loader2, Plus } from 'lucide-react'
import { Sidebar } from './dashboard-mockup/Sidebar'
import { MetricCards } from './dashboard-mockup/MetricCards'
import { RevenueChart } from './dashboard-mockup/RevenueChart'
import { RecentActivity } from './dashboard-mockup/RecentActivity'

interface DashboardMockupProps {
  imageUrl?: string | null
  className?: string
  onUploadClick?: () => void
  isUploading?: boolean
  readOnly?: boolean
}

export const DashboardMockup = ({
  imageUrl,
  className,
  onUploadClick,
  isUploading = false,
  readOnly = false,
}: DashboardMockupProps) => {
  const hasImage = !!imageUrl
  const isEditable = !readOnly && !!onUploadClick

  return (
    <div
      className={cn(
        'relative w-full rounded-xl border border-slate-200/60 bg-white/50 backdrop-blur-xl shadow-2xl overflow-hidden transition-all duration-300',
        isEditable && 'group',
        className,
      )}
      onClick={isEditable ? onUploadClick : undefined}
    >
      {/* Window Header */}
      <div className="h-10 border-b border-slate-200/60 bg-white/40 flex items-center justify-between px-4 sticky top-0 z-20">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-400 border border-red-500/20" />
          <div className="w-3 h-3 rounded-full bg-amber-400 border border-amber-500/20" />
          <div className="w-3 h-3 rounded-full bg-green-400 border border-green-500/20" />
        </div>

        <div className="hidden sm:flex items-center flex-1 max-w-md mx-4 bg-slate-50/50 rounded-md border border-slate-200/50 h-7 px-3 transition-colors hover:bg-slate-100/50">
          <Search className="w-3.5 h-3.5 text-slate-400 mr-2" />
          <div className="text-[10px] text-slate-400 font-medium">
            Pesquisar...
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Bell className="w-4 h-4 text-slate-400 hover:text-slate-600 transition-colors cursor-default" />
          <div className="w-6 h-6 rounded-full bg-indigo-100 border border-indigo-200 flex items-center justify-center">
            <span className="text-[10px] font-bold text-indigo-700">ND</span>
          </div>
        </div>
      </div>

      <div className="flex h-[400px] md:h-[600px] relative">
        {hasImage ? (
          /* Static Image View */
          <div className="w-full h-full relative bg-slate-50">
            <img
              src={imageUrl}
              alt="Dashboard Preview"
              className="w-full h-full object-cover object-top"
            />
          </div>
        ) : (
          /* Dynamic Component Layout */
          <>
            <Sidebar />
            <div className="flex-1 bg-slate-50/50 relative overflow-hidden flex flex-col">
              {/* Internal App Header */}
              <div className="h-14 border-b border-slate-200/60 bg-white/20 flex items-center justify-between px-4 md:px-8">
                <h2 className="font-semibold text-slate-800 text-lg">
                  Painel de Controlo
                </h2>
                <div className="flex items-center gap-2">
                  <div className="hidden md:flex px-3 py-1.5 bg-white rounded-md text-xs font-medium text-slate-600 border border-slate-200 shadow-sm">
                    Hoje, 24 Out
                  </div>
                  <div className="p-1.5 bg-primary text-primary-foreground rounded-md shadow-sm hover:bg-primary/90 transition-colors cursor-default">
                    <Plus className="w-4 h-4" />
                  </div>
                </div>
              </div>

              {/* Viewport Content */}
              <div className="flex-1 p-4 md:p-8 overflow-y-auto">
                <div className="space-y-4 animate-in fade-in duration-700 slide-in-from-bottom-4">
                  <MetricCards />
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
                    <RevenueChart />
                    <RecentActivity />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Editable Overlay - Only visible when editable */}
        {isEditable && (
          <div
            className={cn(
              'absolute inset-0 bg-slate-900/0 flex items-center justify-center transition-all duration-200 z-30 cursor-pointer',
              'group-hover:bg-slate-900/40 backdrop-blur-[1px]',
            )}
          >
            <div
              className={cn(
                'bg-white text-slate-900 px-6 py-4 rounded-full shadow-xl transform scale-95 opacity-0 transition-all duration-200 flex items-center gap-3 font-medium border border-slate-200',
                'group-hover:scale-100 group-hover:opacity-100',
              )}
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span>A carregar...</span>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 text-primary" />
                  <span>
                    {hasImage
                      ? 'Alterar Imagem do Painel'
                      : 'Carregar Imagem do Painel'}
                  </span>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
